import React, { useState, useEffect, useCallback } from 'react';
import { Search, ExpandMore, ExpandLess, Add, Edit, Delete, Save, Close, AddCircleOutline } from '@mui/icons-material';
import { auth } from '../../sp-firebase/firebase';
import { useAppSession } from '../../contexts/AppSessionContext';
import { User } from 'firebase/auth';

const API_URL = (import.meta as any).env?.VITE_API_URL || 'http://localhost:8080';

// ── Types ────────────────────────────────────────────────────

interface MappingEntry {
  id: number;
  sapCode: string;
  formulaCode: string;
  formulaName: string | null;
  description: string | null;
  mapType: string;
  unitWeightKg: number | null;
  needsReview: boolean;
  customerName: string | null;
}

interface MappingMap {
  mapName: string;
  description: string;
  entries: MappingEntry[];
}

interface WorkerMappings {
  workerName: string;
  label: string;
  accountId: number;
  accountName: string;
  maps: MappingMap[];
}

// ── API helpers ──────────────────────────────────────────────

async function waitForFirebaseUser(timeoutMs = 5000): Promise<User | null> {
  if (auth.currentUser) return auth.currentUser;
  return new Promise((resolve) => {
    const timer = setTimeout(() => resolve(null), timeoutMs);
    const unsub = auth.onAuthStateChanged((u) => {
      clearTimeout(timer);
      unsub();
      resolve(u);
    });
  });
}

async function getToken(): Promise<string> {
  const user = await waitForFirebaseUser();
  if (!user) throw new Error('Not authenticated');
  return user.getIdToken();
}

async function fetchMaterialMappings(): Promise<WorkerMappings[]> {
  const token = await getToken();
  const res = await fetch(`${API_URL}/api/v2/import/material-mappings`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error('Failed to fetch material mappings');
  const data = await res.json();
  return data.workers || [];
}

async function createMapping(body: {
  accountId: number; workerName: string; sourceKey: string;
  formulaCode: string; mapType: string; description?: string;
  unitWeightKg?: number | null; needsReview?: boolean;
  customerName?: string | null;
}): Promise<number> {
  const token = await getToken();
  const res = await fetch(`${API_URL}/api/v2/import/material-mappings`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error('Failed to create mapping');
  const data = await res.json();
  return data.id;
}

async function updateMapping(id: number, body: {
  sourceKey?: string; formulaCode?: string; description?: string;
  unitWeightKg?: number | null; needsReview?: boolean;
  customerName?: string | null;
}): Promise<void> {
  const token = await getToken();
  const res = await fetch(`${API_URL}/api/v2/import/material-mappings/${id}`, {
    method: 'PATCH',
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error('Failed to update mapping');
}

async function deleteMapping(id: number): Promise<void> {
  const token = await getToken();
  const res = await fetch(`${API_URL}/api/v2/import/material-mappings/${id}`, {
    method: 'DELETE',
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error('Failed to delete mapping');
}

// ── Inline edit row ──────────────────────────────────────────

interface EditState {
  id: number | null; // null = new row
  sourceKey: string;
  formulaCode: string;
  description: string;
  unitWeightKg: string; // stored as string for input, parsed on save
  needsReview: boolean;
  customerName: string;
}

// ── Component ────────────────────────────────────────────────

const WORKER_OPTIONS = [
  { value: 'carmit', label: 'Carmit' },
  { value: 'strauss', label: 'Strauss' },
  { value: 'admis-pdf', label: 'ADMIS PDF' },
  { value: 'israco', label: 'Israco' },
];

const MAP_TYPE_OPTIONS = [
  { value: 'material', label: 'Material' },
  { value: 'ratio_po', label: 'Ratio PO' },
  { value: 'contract', label: 'Contract / PO' },
  { value: 'inventory', label: 'Inventory' },
  { value: 'commodity', label: 'Commodity' },
];

const MaterialMappings: React.FC = () => {
  const { user: sessionUser, firebaseReady } = useAppSession();
  const selectedAccountId = sessionUser?.accountId ? parseInt(sessionUser.accountId) : null;

  const [workers, setWorkers] = useState<WorkerMappings[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});
  const [editState, setEditState] = useState<{ sectionKey: string; edit: EditState } | null>(null);
  const [saving, setSaving] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newMapping, setNewMapping] = useState({ workerName: 'carmit', sourceKey: '', formulaCode: '', mapType: 'material', description: '', unitWeightKg: '', needsReview: false, customerName: '' });

  const scrollRef = React.useRef<number | null>(null);

  const reload = useCallback((preserveScroll = false) => {
    if (preserveScroll) scrollRef.current = window.scrollY;
    setLoading(true);
    fetchMaterialMappings()
      .then((data) => {
        setWorkers(data);
        if (scrollRef.current != null) {
          requestAnimationFrame(() => window.scrollTo(0, scrollRef.current!));
          scrollRef.current = null;
        }
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { if (firebaseReady) reload(); }, [reload, firebaseReady]);

  const toggleCollapse = (key: string) => {
    setCollapsed((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const q = search.toLowerCase().trim();

  const totalEntries = workers.reduce(
    (sum, w) => sum + w.maps.reduce((s, m) => s + m.entries.length, 0),
    0,
  );

  // ── Edit handlers ──

  const startEdit = (sectionKey: string, entry: MappingEntry) => {
    setEditState({
      sectionKey,
      edit: {
        id: entry.id,
        sourceKey: entry.sapCode,
        formulaCode: entry.formulaCode,
        description: entry.description || '',
        unitWeightKg: entry.unitWeightKg != null ? String(entry.unitWeightKg) : '',
        needsReview: entry.needsReview,
        customerName: entry.customerName || '',
      },
    });
  };

  const startAdd = (sectionKey: string) => {
    setEditState({
      sectionKey,
      edit: { id: null, sourceKey: '', formulaCode: '', description: '', unitWeightKg: '', needsReview: false, customerName: '' },
    });
    // Make sure the section is not collapsed
    setCollapsed((prev) => ({ ...prev, [sectionKey]: false }));
  };

  const cancelEdit = () => setEditState(null);

  const handleSave = async (worker: WorkerMappings, mapType: string) => {
    if (!editState) return;
    const { edit } = editState;
    if (!edit.sourceKey.trim() || !edit.formulaCode.trim()) return;

    setSaving(true);
    try {
      const parsedWeight = edit.unitWeightKg.trim() !== '' ? parseFloat(edit.unitWeightKg) : null;
      const weightVal = parsedWeight != null && !isNaN(parsedWeight) ? parsedWeight : null;

      if (edit.id !== null) {
        await updateMapping(edit.id, {
          sourceKey: edit.sourceKey.trim(),
          formulaCode: edit.formulaCode.trim(),
          description: edit.description.trim() || undefined,
          unitWeightKg: weightVal,
          needsReview: edit.needsReview,
          customerName: edit.customerName.trim() || null,
        });
      } else {
        await createMapping({
          accountId: worker.accountId,
          workerName: worker.workerName,
          sourceKey: edit.sourceKey.trim(),
          formulaCode: edit.formulaCode.trim(),
          mapType,
          description: edit.description.trim() || undefined,
          unitWeightKg: weightVal,
          needsReview: edit.needsReview,
          customerName: edit.customerName.trim() || null,
        });
      }
      setEditState(null);
      reload(true);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Delete this mapping?')) return;
    try {
      await deleteMapping(id);
      reload(true);
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleAddNew = async () => {
    if (!selectedAccountId || !newMapping.sourceKey.trim() || !newMapping.formulaCode.trim()) return;
    setSaving(true);
    try {
      const parsedWt = newMapping.unitWeightKg.trim() !== '' ? parseFloat(newMapping.unitWeightKg) : null;
      const wtVal = parsedWt != null && !isNaN(parsedWt) ? parsedWt : null;
      await createMapping({
        accountId: selectedAccountId,
        workerName: newMapping.workerName,
        sourceKey: newMapping.sourceKey.trim(),
        formulaCode: newMapping.formulaCode.trim(),
        mapType: newMapping.mapType,
        description: newMapping.description.trim() || undefined,
        unitWeightKg: wtVal,
        needsReview: newMapping.needsReview,
        customerName: newMapping.customerName.trim() || null,
      });
      setNewMapping({ workerName: 'carmit', sourceKey: '', formulaCode: '', mapType: 'material', description: '', unitWeightKg: '', needsReview: false, customerName: '' });
      setShowAddForm(false);
      reload(true);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-6 max-w-5xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 text-left">Material Mappings</h1>
        <p className="text-sm text-gray-500 mt-1">
          Source codes mapped to SpotLock formulas per import worker &middot; {totalEntries} entries
        </p>
      </div>

      {/* Add new mapping button + form */}
      <div className="mb-4 flex items-center gap-3">
        <button
          onClick={() => setShowAddForm(!showAddForm)}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-blue-700 bg-white border border-blue-300 rounded-lg hover:bg-blue-50 transition-colors"
        >
          <AddCircleOutline style={{ width: 16, height: 16 }} />
          Add New Mapping
        </button>
        {selectedAccountId && (
          <span className="text-xs text-gray-400">for selected account #{selectedAccountId}</span>
        )}
      </div>

      {showAddForm && (
        <div className="bg-white border border-blue-200 rounded-lg p-4 mb-5 max-w-2xl" style={{ backgroundColor: '#f0f9ff' }}>
          <div className="grid grid-cols-2 gap-3 mb-3">
            <div>
              <label className="text-xs font-medium text-gray-600 mb-1 block">Worker</label>
              <select
                value={newMapping.workerName}
                onChange={(e) => setNewMapping({ ...newMapping, workerName: e.target.value })}
                className="w-full px-2 py-1.5 text-sm border rounded"
              >
                {WORKER_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600 mb-1 block">Map Type</label>
              <select
                value={newMapping.mapType}
                onChange={(e) => setNewMapping({ ...newMapping, mapType: e.target.value })}
                className="w-full px-2 py-1.5 text-sm border rounded"
              >
                {MAP_TYPE_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600 mb-1 block">Source Key</label>
              <input
                value={newMapping.sourceKey}
                onChange={(e) => setNewMapping({ ...newMapping, sourceKey: e.target.value })}
                className="w-full px-2 py-1.5 text-sm font-mono border rounded"
                placeholder="SAP code or commodity"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600 mb-1 block">Formula Code</label>
              <input
                value={newMapping.formulaCode}
                onChange={(e) => setNewMapping({ ...newMapping, formulaCode: e.target.value })}
                className="w-full px-2 py-1.5 text-sm font-mono border rounded"
                placeholder="e.g. RFVCBLEUD"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3 mb-3">
            <div>
              <label className="text-xs font-medium text-gray-600 mb-1 block">Description (optional)</label>
              <input
                value={newMapping.description}
                onChange={(e) => setNewMapping({ ...newMapping, description: e.target.value })}
                className="w-full px-2 py-1.5 text-sm border rounded"
                placeholder="Human-readable label"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600 mb-1 block">Customer (optional)</label>
              <input
                value={newMapping.customerName}
                onChange={(e) => setNewMapping({ ...newMapping, customerName: e.target.value })}
                className="w-full px-2 py-1.5 text-sm border rounded"
                placeholder="Assigned customer name"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600 mb-1 block">Unit Weight (kg)</label>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  step="0.01"
                  value={newMapping.unitWeightKg}
                  onChange={(e) => setNewMapping({ ...newMapping, unitWeightKg: e.target.value })}
                  className="w-28 px-2 py-1.5 text-sm border rounded"
                  placeholder="e.g. 1.0"
                />
                <label className="flex items-center gap-1 text-xs text-gray-500 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={newMapping.needsReview}
                    onChange={(e) => setNewMapping({ ...newMapping, needsReview: e.target.checked })}
                    className="w-3.5 h-3.5"
                  />
                  Needs review
                </label>
              </div>
            </div>
          </div>
          <div className="flex gap-2">
            <button
              onClick={handleAddNew}
              disabled={saving || !selectedAccountId || !newMapping.sourceKey.trim() || !newMapping.formulaCode.trim()}
              className="px-3 py-1.5 text-sm font-medium text-white rounded-lg disabled:opacity-40 transition-colors"
              style={{ backgroundColor: '#2563eb' }}
            >
              {saving ? 'Saving...' : 'Save'}
            </button>
            <button
              onClick={() => setShowAddForm(false)}
              className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-800"
            >
              Cancel
            </button>
            {!selectedAccountId && (
              <span className="text-xs text-red-500 self-center">Select an account first</span>
            )}
          </div>
        </div>
      )}

      {/* Search */}
      <div className="mb-5 relative max-w-sm">
        <Search
          className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
          style={{ width: 18, height: 18 }}
        />
        <input
          type="text"
          placeholder="Search code or formula..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-200 focus:border-blue-400"
        />
      </div>

      {loading && (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
        </div>
      )}

      {error && (
        <div style={{ backgroundColor: '#fef2f2', color: '#b91c1c' }} className="p-3 rounded-lg text-sm mb-4 flex items-center justify-between">
          <span>{error}</span>
          <button onClick={() => setError(null)} className="ml-2 text-xs underline">dismiss</button>
        </div>
      )}

      {!loading && !error && workers.map((worker) => {
        const workerKey = `${worker.workerName}:${worker.accountId}`;

        return (
          <div key={workerKey} className="mb-6">
            {/* Worker heading */}
            <h2 className="text-lg font-semibold text-gray-800 mb-1">{worker.label}</h2>
            <p className="text-xs text-gray-400 mb-3">{worker.accountName} (#{worker.accountId})</p>

            {worker.maps.map((map) => {
              const sectionKey = `${workerKey}-${map.mapName}`;
              const isCollapsed = collapsed[sectionKey] ?? false;

              // Determine the mapType from the first entry
              const mapType = map.entries[0]?.mapType || 'material';

              // Filter entries by search
              const filtered = q
                ? map.entries.filter(
                    (e) =>
                      e.sapCode.toLowerCase().includes(q) ||
                      e.formulaCode.toLowerCase().includes(q) ||
                      (e.formulaName && e.formulaName.toLowerCase().includes(q)) ||
                      (e.description && e.description.toLowerCase().includes(q)) ||
                      (e.customerName && e.customerName.toLowerCase().includes(q)),
                  )
                : map.entries;

              // Group by formulaCode for visual grouping
              const byFormula = new Map<string, MappingEntry[]>();
              for (const e of filtered) {
                const arr = byFormula.get(e.formulaCode) || [];
                arr.push(e);
                byFormula.set(e.formulaCode, arr);
              }

              const isAdding = editState?.sectionKey === sectionKey && editState.edit.id === null;
              const sourceLabel = worker.workerName === 'admis-pdf' ? 'Commodity' : 'SAP Code';

              return (
                <div
                  key={sectionKey}
                  className="bg-white rounded-lg border border-gray-200 mb-3 overflow-hidden"
                >
                  {/* Map header — clickable */}
                  <button
                    onClick={() => toggleCollapse(sectionKey)}
                    className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-gray-50 transition-colors"
                  >
                    <div>
                      <span className="text-sm font-medium text-gray-700">{map.mapName}</span>
                      <span className="text-xs text-gray-400 ml-2">{map.description}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-gray-400">
                        {filtered.length} {filtered.length === 1 ? 'entry' : 'entries'}
                      </span>
                      {isCollapsed ? (
                        <ExpandMore className="w-5 h-5 text-gray-400" />
                      ) : (
                        <ExpandLess className="w-5 h-5 text-gray-400" />
                      )}
                    </div>
                  </button>

                  {/* Table */}
                  {!isCollapsed && (
                    <div>
                      <table className="w-full">
                        <thead>
                          <tr className="bg-gray-50 text-left text-[11px] font-medium text-gray-500 uppercase tracking-wider">
                            <th className="px-4 py-2">{sourceLabel}</th>
                            <th className="px-4 py-2">Formula Code</th>
                            <th className="px-4 py-2">Formula Name</th>
                            <th className="px-4 py-2">Description</th>
                            <th className="px-4 py-2">Customer</th>
                            <th className="px-4 py-2">Wt (kg)</th>
                            <th className="px-4 py-2 w-20">Actions</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                          {filtered.length === 0 && !isAdding ? (
                            <tr>
                              <td colSpan={7} className="px-4 py-6 text-center text-sm text-gray-400">
                                {q ? 'No matches' : 'No entries'}
                              </td>
                            </tr>
                          ) : (
                            Array.from(byFormula.entries()).map(([, entries], groupIdx) =>
                              entries.map((entry, idx) => {
                                const isEditing = editState?.sectionKey === sectionKey && editState.edit.id === entry.id;

                                if (isEditing) {
                                  return (
                                    <tr key={entry.id} style={{ backgroundColor: '#eff6ff' }}>
                                      <td className="px-4 py-1">
                                        <input
                                          value={editState!.edit.sourceKey}
                                          onChange={(e) => setEditState({ ...editState!, edit: { ...editState!.edit, sourceKey: e.target.value } })}
                                          className="w-full px-2 py-1 text-sm font-mono border rounded"
                                        />
                                      </td>
                                      <td className="px-4 py-1">
                                        <input
                                          value={editState!.edit.formulaCode}
                                          onChange={(e) => setEditState({ ...editState!, edit: { ...editState!.edit, formulaCode: e.target.value } })}
                                          className="w-full px-2 py-1 text-sm font-mono border rounded"
                                        />
                                      </td>
                                      <td className="px-4 py-1 text-sm text-gray-400">—</td>
                                      <td className="px-4 py-1">
                                        <input
                                          value={editState!.edit.description}
                                          onChange={(e) => setEditState({ ...editState!, edit: { ...editState!.edit, description: e.target.value } })}
                                          className="w-full px-2 py-1 text-sm border rounded"
                                          placeholder="optional"
                                        />
                                      </td>
                                      <td className="px-4 py-1">
                                        <input
                                          value={editState!.edit.customerName}
                                          onChange={(e) => setEditState({ ...editState!, edit: { ...editState!.edit, customerName: e.target.value } })}
                                          className="w-full px-2 py-1 text-sm border rounded"
                                          placeholder="optional"
                                        />
                                      </td>
                                      <td className="px-4 py-1">
                                        <div className="flex items-center gap-1">
                                          <input
                                            type="number"
                                            step="0.01"
                                            value={editState!.edit.unitWeightKg}
                                            onChange={(e) => setEditState({ ...editState!, edit: { ...editState!.edit, unitWeightKg: e.target.value } })}
                                            className="w-20 px-2 py-1 text-sm border rounded"
                                            placeholder="kg"
                                          />
                                          <label className="flex items-center gap-1 text-[10px] text-gray-500 cursor-pointer whitespace-nowrap" title="Needs review">
                                            <input
                                              type="checkbox"
                                              checked={editState!.edit.needsReview}
                                              onChange={(e) => setEditState({ ...editState!, edit: { ...editState!.edit, needsReview: e.target.checked } })}
                                              className="w-3 h-3"
                                            />
                                            review
                                          </label>
                                        </div>
                                      </td>
                                      <td className="px-4 py-1">
                                        <div className="flex gap-1">
                                          <button
                                            onClick={() => handleSave(worker, mapType)}
                                            disabled={saving}
                                            className="p-1 rounded hover:bg-green-100"
                                            title="Save"
                                          >
                                            <Save style={{ width: 16, height: 16, color: '#16a34a' }} />
                                          </button>
                                          <button onClick={cancelEdit} className="p-1 rounded hover:bg-gray-100" title="Cancel">
                                            <Close style={{ width: 16, height: 16, color: '#6b7280' }} />
                                          </button>
                                        </div>
                                      </td>
                                    </tr>
                                  );
                                }

                                return (
                                  <tr
                                    key={entry.id}
                                    className={`${
                                      groupIdx % 2 === 0 ? '' : 'bg-gray-50/50'
                                    } hover:bg-blue-50/40 transition-colors`}
                                  >
                                    <td className="px-4 py-2 font-mono text-sm text-gray-900">
                                      {entry.sapCode}
                                    </td>
                                    <td className="px-4 py-2">
                                      {idx === 0 ? (
                                        <span className="font-mono text-sm font-semibold text-blue-700">
                                          {entry.formulaCode}
                                        </span>
                                      ) : (
                                        <span className="text-xs text-gray-300">↑</span>
                                      )}
                                    </td>
                                    <td className="px-4 py-2 text-sm text-gray-600">
                                      {idx === 0
                                        ? entry.formulaName || (
                                            <span className="text-gray-300 italic">unknown</span>
                                          )
                                        : null}
                                    </td>
                                    <td className="px-4 py-2 text-xs text-gray-400">{entry.description}</td>
                                    <td className="px-4 py-2 text-sm text-gray-600">
                                      {entry.customerName || <span className="text-gray-300">—</span>}
                                    </td>
                                    <td className="px-4 py-2 text-sm text-gray-600">
                                      {entry.unitWeightKg != null ? (
                                        <span style={entry.needsReview ? { color: '#d97706', fontWeight: 500 } : {}}>
                                          {entry.unitWeightKg}
                                          {entry.needsReview && <span className="ml-1 text-[10px] px-1 rounded" style={{ backgroundColor: '#fef3c7', color: '#92400e' }}>review</span>}
                                        </span>
                                      ) : entry.needsReview ? (
                                        <span style={{ color: '#ef4444', fontWeight: 500 }}>missing</span>
                                      ) : (
                                        <span className="text-gray-300">— kg</span>
                                      )}
                                    </td>
                                    <td className="px-4 py-2">
                                      <div className="flex gap-1">
                                        <button
                                          onClick={() => startEdit(sectionKey, entry)}
                                          className="p-1 rounded hover:bg-blue-100"
                                          title="Edit"
                                        >
                                          <Edit style={{ width: 14, height: 14, color: '#3b82f6' }} />
                                        </button>
                                        <button
                                          onClick={() => handleDelete(entry.id)}
                                          className="p-1 rounded hover:bg-red-100"
                                          title="Delete"
                                        >
                                          <Delete style={{ width: 14, height: 14, color: '#ef4444' }} />
                                        </button>
                                      </div>
                                    </td>
                                  </tr>
                                );
                              }),
                            )
                          )}

                          {/* Add new row */}
                          {isAdding && (
                            <tr style={{ backgroundColor: '#f0fdf4' }}>
                              <td className="px-4 py-1">
                                <input
                                  value={editState!.edit.sourceKey}
                                  onChange={(e) => setEditState({ ...editState!, edit: { ...editState!.edit, sourceKey: e.target.value } })}
                                  className="w-full px-2 py-1 text-sm font-mono border rounded"
                                  placeholder={sourceLabel}
                                  autoFocus
                                />
                              </td>
                              <td className="px-4 py-1">
                                <input
                                  value={editState!.edit.formulaCode}
                                  onChange={(e) => setEditState({ ...editState!, edit: { ...editState!.edit, formulaCode: e.target.value } })}
                                  className="w-full px-2 py-1 text-sm font-mono border rounded"
                                  placeholder="Formula code"
                                />
                              </td>
                              <td className="px-4 py-1 text-sm text-gray-400">—</td>
                              <td className="px-4 py-1">
                                <input
                                  value={editState!.edit.description}
                                  onChange={(e) => setEditState({ ...editState!, edit: { ...editState!.edit, description: e.target.value } })}
                                  className="w-full px-2 py-1 text-sm border rounded"
                                  placeholder="Description (optional)"
                                />
                              </td>
                              <td className="px-4 py-1">
                                <input
                                  value={editState!.edit.customerName}
                                  onChange={(e) => setEditState({ ...editState!, edit: { ...editState!.edit, customerName: e.target.value } })}
                                  className="w-full px-2 py-1 text-sm border rounded"
                                  placeholder="Customer (optional)"
                                />
                              </td>
                              <td className="px-4 py-1">
                                <div className="flex items-center gap-1">
                                  <input
                                    type="number"
                                    step="0.01"
                                    value={editState!.edit.unitWeightKg}
                                    onChange={(e) => setEditState({ ...editState!, edit: { ...editState!.edit, unitWeightKg: e.target.value } })}
                                    className="w-20 px-2 py-1 text-sm border rounded"
                                    placeholder="kg"
                                  />
                                  <label className="flex items-center gap-1 text-[10px] text-gray-500 cursor-pointer whitespace-nowrap" title="Needs review">
                                    <input
                                      type="checkbox"
                                      checked={editState!.edit.needsReview}
                                      onChange={(e) => setEditState({ ...editState!, edit: { ...editState!.edit, needsReview: e.target.checked } })}
                                      className="w-3 h-3"
                                    />
                                    review
                                  </label>
                                </div>
                              </td>
                              <td className="px-4 py-1">
                                <div className="flex gap-1">
                                  <button
                                    onClick={() => handleSave(worker, mapType)}
                                    disabled={saving || !editState!.edit.sourceKey.trim() || !editState!.edit.formulaCode.trim()}
                                    className="p-1 rounded hover:bg-green-100 disabled:opacity-30"
                                    title="Save"
                                  >
                                    <Save style={{ width: 16, height: 16, color: '#16a34a' }} />
                                  </button>
                                  <button onClick={cancelEdit} className="p-1 rounded hover:bg-gray-100" title="Cancel">
                                    <Close style={{ width: 16, height: 16, color: '#6b7280' }} />
                                  </button>
                                </div>
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>

                      {/* Add button */}
                      {!isAdding && (
                        <button
                          onClick={() => startAdd(sectionKey)}
                          className="flex items-center gap-1 px-4 py-2 text-xs text-blue-600 hover:text-blue-800 hover:bg-blue-50/50 w-full transition-colors"
                        >
                          <Add style={{ width: 14, height: 14 }} />
                          Add mapping
                        </button>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        );
      })}
    </div>
  );
};

export default MaterialMappings;
